import React from 'react'
import { Tag } from 'antd'
import { useIntl } from 'react-intl'
import { AttributionFormatMessage, formatApiClientAttribution } from '@vacationtracker/shared/public-api/attribution'
import capitalize from 'lodash/capitalize'

import LeaveFormatDate from '../leaves-format-date'
import { isToilLeave } from '@vacationtracker/shared/functions/is-toil-leave-request'

import IntlMessages from '../../util/IntlMessages'
import FormattedDate from '@vacationtracker/shared/components/formatted-date'

import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { DisplayLeaveDuration, displayToilInDaysAndHours } from '@vacationtracker/shared/components/display-leave-in-days-and-hours'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '@vacationtracker/shared/data/app-parameters'
import { computeDaysAndHours, getFormattedDaysAndHours } from '@vacationtracker/shared/functions/get-days-or-hours'
import { LeaveDisplayFormatEnum } from '@vacationtracker/shared/types/display-in-days-hours'

// Stupid fix but works https://stackoverflow.com/questions/60519460/ts2322-type-string-is-not-assignable-to-type-left-center-right-un
declare type AlignType = 'left' | 'center' | 'right'

const columnsIndex = {
  title: '#',
  dataIndex: 'id',
  key: 'id',
  width: 60,
  align: 'center' as AlignType,
  render: (date, row, index) => {
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    return index + 1
  },
}

const columnsStartDate = (locale: LocaleEnum) => ({
  title: <IntlMessages id="components.leavesColumns.dates" />,
  dataIndex: 'startDate',
  key: 'startDate',
  // eslint-disable-next-line react/display-name
  render: (startDate) => {
    return (
      <>
        <FormattedDate value={startDate} format="MMMM Do YYYY." locale={locale} />
      </>
    )
  },
})

const columnsStartEndDate = (locale: LocaleEnum, hourFormat: HourFormatEnum) => ({
  title: <IntlMessages id="components.leavesColumns.dates" />,
  dataIndex: 'startDate',
  key: 'startDate',
  minWidth: 280,
  // eslint-disable-next-line react/display-name
  render: (startDate: string, row) => {
    const hasPartDay = row.partDay

    return (
      <>
        <LeaveFormatDate
          locale={locale}
          hourFormat={hourFormat}
          startDate={startDate}
          endDate={row.endDate}
          isPartDay={row.isPartDay}
          partDayStartHour={hasPartDay ? hasPartDay.startHour : row.partDayStartHour}
          partDayEndHour={hasPartDay ? hasPartDay.endHour : row.partDayEndHour}
          partDayStartMinute={hasPartDay ? hasPartDay.startMinute : 0}
          partDayEndMinute={hasPartDay ? hasPartDay.endMinute : 0}
        />
        {row.isEdited && (
          <Tag color="#7f00ff">
            <IntlMessages id="app.edited" />
          </Tag>
        )}
      </>
    )
  },
})

const columnsLeaveTypeName = {
  title: <IntlMessages id="app.leaveType" />,
  dataIndex: 'leaveType',
  key: 'leaveType',
  width: 180,
  render: (leaveType, row) => {
    return (
      <>
        {leaveType?.name}
        &nbsp;
        {isToilLeave(row.id) && (
          <Tag color={'green'}>
            <IntlMessages id="app.toil" />
          </Tag>
        )}
      </>
    )
  },
}

const columnsDuration = (formatMessage) => ({
  title: <IntlMessages id="components.leavesColumns.duration" />,
  dataIndex: 'workingDays',
  key: 'workingDays',
  width: 90,
  render: (workingDays, row) => {
    if (isToilLeave(row.id)) {
      return <>{displayToilInDaysAndHours(formatMessage, row)}</>
    } else {
      return row.daysList ? (
        <DisplayLeaveDuration daysList={row.daysList} value={workingDays} hasSchedule={row.hasWorkingHoursSchedule} />
      ) : (
        oldLeavesDurationFallback(row.workingDays as number, formatMessage)
      )
    }
  },
})

//old leaves do not have daysList, so their work hours must be default
const oldLeavesDurationFallback = (workingDays: number, formatMessage) => {
  const totalLeaveHours = workingDays * DEFAULT_WORKING_HOURS_IN_DAY
  const leave = computeDaysAndHours(totalLeaveHours, DEFAULT_WORKING_HOURS_IN_DAY, true)

  return (
    <>
      {getFormattedDaysAndHours({
        leave,
        formatMessage,
        displayFormat: LeaveDisplayFormatEnum.long,
      })}
    </>
  )
}

const columnsReason = {
  title: <IntlMessages id="components.leavesColumns.reason" />,
  dataIndex: 'reason',
  key: 'reason',
  width: 260,
  render: (reason) => {
    return <div>{reason || ''}</div>
  },
}

const columnsDenyReason = {
  title: <IntlMessages id="components.leavesColumns.denyReason" />,
  dataIndex: 'statusReason',
  key: 'statusReason',
  render: (statusReason) => {
    return <div>{statusReason || ''}</div>
  },
}

const columnsStatus = (formatMessage) => ({
  title: <IntlMessages id="app.status" />,
  dataIndex: 'status',
  key: 'status',
  width: 88,
  // eslint-disable-next-line react/display-name
  render: (status, row) => {
    let statusColor = ''
    let statusText = ''
    if (status === 'APPROVED') {
      statusColor = 'green'
      statusText = capitalize(formatMessage({ id: 'app.approved' }))
    } else if (status === 'DENIED') {
      statusColor = 'red'
      statusText = capitalize(formatMessage({ id: 'app.denied' }))
    } else if (status === 'CANCELLED') {
      statusColor = 'orange'
      statusText = capitalize(formatMessage({ id: 'app.cancelled' }))
    } else if (status === 'EXPIRED') {
      statusColor = 'orange'
      statusText = capitalize(formatMessage({ id: 'app.expired' }))
    } else if (status === 'DELETED') {
      statusColor = 'magenta'
      statusText = capitalize(formatMessage({ id: 'app.deleted' }))
    } else {
      statusColor = 'black'
      statusText = capitalize(formatMessage({ id: 'app.expired' }))
    }

    if (row.isResent) {
      const resentStatusColor = 'blue'
      const resentStatusText = capitalize(formatMessage({ id: 'app.resent' }))
      return (
        <>
          <Tag style={{ marginBottom: '5px' }} color={statusColor}>
            {statusText}
          </Tag>
          <Tag color={resentStatusColor}>{resentStatusText}</Tag>
        </>
      )
    }
    return <Tag color={statusColor}>{statusText}</Tag>
  },
})

const ReviewerName = ({ name, integrationName }: { name: string; integrationName?: string | null }) => {
  const { formatMessage } = useIntl()
  return <>{formatApiClientAttribution({ name, integrationName: integrationName ?? undefined, formatMessage: formatMessage as AttributionFormatMessage })}</>
}

const columnsApprover = {
  title: <IntlMessages id="components.leavesColumns.reviewedby" />,
  dataIndex: 'approver',
  key: 'approver',
  minWidth: 130,
  render: (approver, row) => {
    if (row.autoApproved) {
      return <IntlMessages id="app.autoApproved" />
    } else if (row.cancelledBeforeReview) {
      return <IntlMessages id="components.leavesColumns.cancelledBeforeReview" />
    } else if (row.approverList && row.approverList.length > 0) {
      return (
        <>
          {row.approverList.map((approver, index) => {
            return (
              <span key={index}>
                {index === 0 ? '' : ', '}
                {/* The integration belongs to the final review, so only the last approver carries it. */}
                <ReviewerName
                  name={approver?.name ? approver?.name : row.approverId}
                  integrationName={index === row.approverList.length - 1 ? row.reviewedThroughIntegration : undefined}
                />
                <>
                  {row.approvedBySubstituteApprover && (
                    <strong>
                      {'('}
                      <IntlMessages id="app.substituteApprover" />
                      {')'}
                    </strong>
                  )}
                </>
              </span>
            )
          })}
        </>
      )
    } else {
      return (
        <>
          <ReviewerName name={approver?.name ? approver?.name : row.approverId} integrationName={row.reviewedThroughIntegration} />
          <div>
            {row.approvedBySubstituteApprover && (
              <strong>
                {'('}
                <IntlMessages id="app.substituteApprover" />
                {')'}
              </strong>
            )}
          </div>
        </>
      )
    }
  },
}

export { columnsIndex, columnsStartDate, columnsStartEndDate, columnsLeaveTypeName, columnsDuration, columnsReason, columnsDenyReason, columnsStatus, columnsApprover }
